import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ExternalLink, RefreshCw, Search, Settings2, TableProperties } from 'lucide-react';
import api from '../../utils/api';

/** Canonical column order from the daily meetings Google Sheet. */
export const DAILY_MEETINGS_COLUMNS = [
    'Created On Date',
    'Created On Time',
    'Name',
    'Phone',
    'Email',
    'Assignee name',
    'Assignee emailid',
    'Status',
    'Lead Temperature',
    'Brand',
    'Lost Reason',
    'Lead Score',
    'City',
    'First Call',
    'Last Call',
    'Total Call Duration',
    'Calls Count',
    'Connected Call Count',
    'Call Connection Rate (%)',
    'Assignee Manager name',
    'Assignee Manager emailid',
    'Assignee Last Changed On',
    'Last Assignee Change From',
    'lead capture count',
    'Unique lead ID',
    'Modified On',
    'Captured On',
    'Status Last Updated On',
    'Last Assigned By',
];

function fmtDateTime(value) {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function normLabel(s) {
    return String(s || '').trim().toLowerCase();
}

function findColumnKey(columns, ...labels) {
    const targets = new Set(labels.map(normLabel));
    return columns.find((c) => targets.has(normLabel(c.label)))?.key || null;
}

/** Simplified status buckets for KPI cards. */
function classifyStatus(raw) {
    const v = String(raw || '').trim().toLowerCase();
    if (!v) return 'other';
    if (v.includes('not qualified') || v === 'lost' || v.startsWith('lost ')) return 'closed_lost';
    if (v === 'new' || v === 'new lead' || v.startsWith('new lead')) return 'not_contacted';
    if (v === 'assigned') return 'not_contacted';
    if (/^attempt\s*\d+$/.test(v)) return 'not_connected';
    if (v === 'connected' || (v.includes('connected') && v.includes('not scheduled'))) return 'connected';
    if (
        v.includes('meeting scheduled')
        || v === 'meeting conducted'
        || v.includes('details shared')
        || v.includes('follow-up')
        || v.includes('follow up')
        || v === 'nurture'
    ) return 'in_pipeline';
    if (v.includes('meeting conducted')) return 'in_pipeline';
    return 'other';
}

function StatTile({ label, value, hint, tone = 'default' }) {
    const toneCls = tone === 'amber' ? 'ring-amber-100 bg-amber-50/40'
        : tone === 'teal' ? 'ring-brand-teal/15 bg-brand-teal-50/30'
            : tone === 'rose' ? 'ring-rose-100 bg-rose-50/40'
                : 'ring-ink-100/80 bg-white';
    return (
        <div className={`rounded-xl px-4 py-3 ring-1 ${toneCls}`}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500">{label}</div>
            <div className="mt-1 text-xl font-bold text-ink-900 tabular-nums">{value}</div>
            {hint && <div className="mt-1 text-[11px] text-ink-400">{hint}</div>}
        </div>
    );
}

function StatusBadge({ value }) {
    if (!value) return <span className="text-ink-300">-</span>;
    const group = classifyStatus(value);
    const cls = group === 'not_contacted' ? 'bg-slate-100 text-slate-800'
        : group === 'not_connected' ? 'bg-amber-100 text-amber-800'
        : group === 'connected' ? 'bg-sky-100 text-sky-800'
            : group === 'in_pipeline' ? 'bg-emerald-100 text-emerald-800'
                : group === 'closed_lost' ? 'bg-rose-100 text-rose-800'
                    : 'bg-ink-100 text-ink-700';
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
            {value}
        </span>
    );
}

function TempBadge({ value }) {
    if (!value) return <span className="text-ink-300">-</span>;
    const v = String(value).toLowerCase();
    let cls = 'bg-ink-100 text-ink-700';
    if (v.includes('hot')) cls = 'bg-rose-100 text-rose-800';
    else if (v.includes('warm')) cls = 'bg-orange-100 text-orange-800';
    else if (v.includes('cold')) cls = 'bg-sky-100 text-sky-800';
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
            {value}
        </span>
    );
}

function CellValue({ column, value }) {
    const label = normLabel(column.label);
    if (!value) return <span className="text-ink-300">-</span>;
    if (label === 'status') return <StatusBadge value={value} />;
    if (label === 'lead temperature') return <TempBadge value={value} />;
    if (label === 'email' || label.includes('email')) {
        return <span className="text-xs text-ink-600 break-all">{value}</span>;
    }
    if (label === 'phone') {
        return <span className="font-mono text-xs tabular-nums">{value}</span>;
    }
    return <span className="text-sm text-ink-700">{value}</span>;
}

export default function LiveSheetBoard({ onBack }) {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [payload, setPayload] = useState({ rows: [], columns: [], fetchedAt: '', sourceUrl: '' });
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [assigneeFilter, setAssigneeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const load = async ({ silent = false } = {}) => {
        if (silent) setRefreshing(true);
        else setLoading(true);
        try {
            const { data } = await api.get('/dashboard/sheet-feed', { silent: true });
            setPayload({
                rows: Array.isArray(data.rows) ? data.rows : [],
                columns: Array.isArray(data.columns) ? data.columns : [],
                fetchedAt: data.fetchedAt || '',
                sourceUrl: data.sourceUrl || '',
            });
            setError('');
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to load daily meetings sheet');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const orderedColumns = useMemo(() => {
        const byLabel = new Map(payload.columns.map((c) => [normLabel(c.label), c]));
        const ordered = DAILY_MEETINGS_COLUMNS
            .map((label) => byLabel.get(normLabel(label)))
            .filter(Boolean);
        const used = new Set(ordered.map((c) => c.key));
        const rest = payload.columns.filter((c) => !used.has(c.key));
        return [...ordered, ...rest];
    }, [payload.columns]);

    const assigneeKey = useMemo(() => findColumnKey(payload.columns, 'Assignee name'), [payload.columns]);
    const statusKey = useMemo(() => findColumnKey(payload.columns, 'Status'), [payload.columns]);

    const assigneeOptions = useMemo(() => {
        if (!assigneeKey) return [];
        return [...new Set(payload.rows.map((r) => r[assigneeKey]).filter(Boolean))].sort();
    }, [payload.rows, assigneeKey]);

    const statusOptions = useMemo(() => {
        if (!statusKey) return [];
        return [...new Set(payload.rows.map((r) => r[statusKey]).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    }, [payload.rows, statusKey]);

    const filteredRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return payload.rows.filter((row) => {
            if (assigneeFilter && assigneeKey && row[assigneeKey] !== assigneeFilter) return false;
            if (statusFilter && statusKey && row[statusKey] !== statusFilter) return false;
            if (!needle) return true;
            return orderedColumns.some((col) => String(row[col.key] || '').toLowerCase().includes(needle));
        });
    }, [payload.rows, orderedColumns, assigneeFilter, statusFilter, assigneeKey, statusKey, search]);

    const stats = useMemo(() => {
        const total = filteredRows.length;
        const buckets = { not_contacted: 0, not_connected: 0, connected: 0, in_pipeline: 0, closed_lost: 0 };
        if (statusKey) {
            for (const row of filteredRows) {
                const group = classifyStatus(row[statusKey]);
                if (buckets[group] != null) buckets[group] += 1;
            }
        }
        return {
            total,
            notContacted: buckets.not_contacted,
            pending: buckets.not_connected,
            connected: buckets.connected,
            completed: buckets.in_pipeline,
            lost: buckets.closed_lost,
        };
    }, [filteredRows, statusKey]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

    useEffect(() => {
        setPage(1);
    }, [search, pageSize, assigneeFilter, statusFilter, payload.fetchedAt]);

    const pagedRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, page, pageSize]);

    const selectCls = 'w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

    return (
        <div className="space-y-4 pb-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <button type="button" onClick={onBack} className="ag-btn text-xs py-1.5 mb-2">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
                    </button>
                    <div className="flex items-center gap-2">
                        <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-teal-50 text-brand-teal-700 ring-1 ring-brand-teal/10">
                            <TableProperties className="h-5 w-5" />
                        </span>
                        <div>
                            <h1 className="text-xl font-bold text-ink-900">Daily Meeting Dashboard</h1>
                            <p className="text-sm text-ink-500 mt-0.5">
                                Overview of daily meeting assignments and performance
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {/* {payload.sourceUrl && (
                        <a href={payload.sourceUrl} target="_blank" rel="noreferrer" className="ag-btn-ghost text-xs py-1.5">
                            Open sheet <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                    )} */}
                    <button
                        type="button"
                        onClick={() => load({ silent: true })}
                        disabled={loading || refreshing}
                        className="ag-btn-primary text-xs py-1.5"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatTile label="Total leads" value={stats.total} tone="teal" />
                <StatTile label="Not contacted" value={stats.notContacted} />
                <StatTile label="Not connected" value={stats.pending} tone="amber" />
                <StatTile label="Connected" value={stats.connected}  />
                <StatTile label="In pipeline" value={stats.completed} tone="teal" />
                <StatTile label="Closed lost" value={stats.lost} tone="rose" />
            </div>

            <section className="rounded-2xl bg-white ring-1 ring-ink-100/80 overflow-hidden shadow-soft">
                <div className="px-4 py-3 border-b border-ink-100 bg-gradient-to-r from-white to-brand-teal-50/20">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                            {/* <h2 className="text-sm font-semibold text-ink-900">Total</h2> */}
                            <span className="inline-flex items-center rounded-full bg-brand-teal-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-brand-teal-800 ring-1 ring-brand-teal/15">
                                {filteredRows.length.toLocaleString('en-IN')} {filteredRows.length === 1 ? 'row' : 'rows'}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <label className="flex-1 min-w-[200px]">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500">Search</span>
                            <div className="relative mt-1.5">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                                <input
                                    className="ag-input w-full pl-9 text-sm py-2.5"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by name, phone, email…"
                                />
                            </div>
                        </label>

                        {assigneeKey ? (
                            <label className="min-w-[160px]">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500">Assignee</span>
                                <select className={`mt-1.5 ${selectCls}`} value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
                                    <option value="">All assignees</option>
                                    {assigneeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </label>
                        ) : null}

                        {statusKey && (
                            <label className="min-w-[160px]">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500">Status</span>
                                <select className={`mt-1.5 ${selectCls}`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="">All statuses</option>
                                    {statusOptions.map((o) => (
                                        <option key={o} value={o}>{o}</option>
                                    ))}
                                </select>
                            </label>
                        )}

                        <label className="min-w-[100px]">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500">Per page</span>
                            <select className={`mt-1.5 ${selectCls}`} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                    </div>
                    {payload.fetchedAt && (
                        <p className="text-[10px] text-ink-400 mt-2">Last synced {fmtDateTime(payload.fetchedAt)} · {filteredRows.length} of {payload.rows.length} rows</p>
                    )}
                </div>

                {loading ? (
                    <div className="px-4 py-16 text-center text-sm text-ink-400">Loading daily meetings…</div>
                ) : error ? (
                    <div className="px-4 py-8">
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                            <div className="flex items-center gap-2 font-medium">
                                <Settings2 className="h-4 w-4" />
                                Could not load daily meetings sheet
                            </div>
                            <p className="mt-1 text-xs leading-relaxed">{error}</p>
                        </div>
                    </div>
                ) : orderedColumns.length === 0 ? (
                    <div className="px-4 py-16 text-center text-sm text-ink-400">No data in sheet.</div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="bg-ink-50/90">
                                        {orderedColumns.map((column) => (
                                            <th
                                                key={column.key}
                                                className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-ink-500 whitespace-nowrap border-b border-ink-100"
                                            >
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-ink-100">
                                    {pagedRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={orderedColumns.length} className="px-4 py-12 text-center text-sm text-ink-400">
                                                No rows match the current filters.
                                            </td>
                                        </tr>
                                    ) : pagedRows.map((row) => (
                                        <tr key={row._row} className="hover:bg-brand-teal-50/15">
                                            {orderedColumns.map((column) => (
                                                <td
                                                    key={column.key}
                                                    className="px-3 py-2.5 align-top whitespace-nowrap min-w-[120px] max-w-[280px]"
                                                >
                                                    <CellValue column={column} value={row[column.key]} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-ink-100 bg-ink-50/40">
                            <p className="text-xs text-ink-500">
                                Showing {(page - 1) * pageSize + (pagedRows.length ? 1 : 0)}–{(page - 1) * pageSize + pagedRows.length} of {filteredRows.length}
                            </p>
                            <div className="flex items-center gap-2">
                                <button type="button" className="ag-btn text-xs py-1.5" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                                    Previous
                                </button>
                                <span className="text-xs font-medium text-ink-500">Page {page} / {totalPages}</span>
                                <button type="button" className="ag-btn text-xs py-1.5" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
}
